import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import toast from '@/lib/toast';
import type { CustomerNote, CreateNoteRequest } from '@/lib/types';

// Query key factory
export const customerNotesKeys = {
  all: ['customer-notes'] as const,
  byCustomer: (accountId: string, email: string) =>
    [...customerNotesKeys.all, accountId, email] as const,
};

// Fetch customer notes
export function useCustomerNotes(accountId: string, email: string) {
  return useQuery({
    queryKey: customerNotesKeys.byCustomer(accountId, email),
    queryFn: async () => {
      const response = await apiClient.getCustomerNotes(email);
      return response.notes as CustomerNote[];
    },
    enabled: !!accountId && !!email,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Create customer note
export function useCreateCustomerNote(accountId: string, email: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateNoteRequest) => {
      const response = await apiClient.createCustomerNote(email, data);
      return response.note as CustomerNote;
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: customerNotesKeys.byCustomer(accountId, email)
      });
      toast.success('Note saved', {
        description: 'Customer note has been saved successfully.',
      });
    },
    onError: (error) => {
      console.error('Failed to create note:', error);
      toast.error('Failed to save note', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });
}

// Update customer note
export function useUpdateCustomerNote(accountId: string, email: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, note }: { id: string; note: string }) => {
      const response = await apiClient.updateCustomerNote(id, note);
      return response.note;
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: customerNotesKeys.byCustomer(accountId, email)
      });
      toast.success('Note updated', {
        description: 'Customer note has been updated successfully.',
      });
    },
    onError: (error) => {
      console.error('Failed to update note:', error);
      toast.error('Failed to update note', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });
}

// Delete customer note
export function useDeleteCustomerNote(accountId: string, email: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: string) => {
      await apiClient.deleteCustomerNote(noteId);
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: customerNotesKeys.byCustomer(accountId, email)
      });
      toast.success('Note deleted', {
        description: 'Customer note has been deleted successfully.',
      });
    },
    onError: (error) => {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });
}
